import { useId, type InputHTMLAttributes, type JSX } from "react";
import {
  FieldErrorMessage,
  RequiredMark,
  fieldLabelClassName,
  fieldDescriptionClassName,
  fieldLegendClassName,
  fieldFocusCn,
} from "./fieldHelpers";
import { cn } from "packages/dsaui/src/utils";

export interface RadioOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface RadioGroupProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type" | "children" | "value" | "defaultValue"
> {
  name: string;
  options: RadioOption[];
  value?: string;
  defaultValue?: string;
  errorMessage?: string | null;
  description?: string;
  legend?: string;
  inline?: boolean;
}

export const RadioGroup = ({
  name,
  options,
  value,
  defaultValue,
  errorMessage,
  description,
  legend,
  required,
  inline = false,
  disabled,
  className,
  ...props
}: RadioGroupProps): JSX.Element => {
  const groupId = useId();
  const descriptionId = `${name}-description`;
  const errorId = `${name}-error`;
  const isControlled = typeof value !== "undefined";

  return (
    <fieldset
      className={cn("mb-5 min-w-0 border-0 p-0", className)}
      aria-invalid={errorMessage ? "true" : undefined}
    >
      {legend ? (
        <legend className={fieldLegendClassName}>
          {legend}
          <RequiredMark required={required} />
        </legend>
      ) : null}

      {description ? (
        <p id={descriptionId} className={fieldDescriptionClassName}>
          {description}
        </p>
      ) : null}

      <div
        className={cn(
          "mt-3 flex flex-col gap-2",
          inline && "sm:flex-row sm:flex-wrap sm:gap-4",
        )}
      >
        {options.map((option) => {
          const optionId = `${groupId}-${name}-${option.value}`;
          const optionDisabled = disabled || option.disabled;

          return (
            <label
              key={optionId}
              className={cn(
                "flex cursor-pointer items-center gap-2",
                optionDisabled && "cursor-not-allowed opacity-60",
              )}
              htmlFor={optionId}
            >
              <input
                {...props}
                id={optionId}
                name={name}
                type="radio"
                value={option.value}
                checked={isControlled ? value === option.value : undefined}
                defaultChecked={
                  isControlled ? undefined : defaultValue === option.value
                }
                disabled={optionDisabled}
                required={required}
                aria-describedby={
                  cn(description && descriptionId, errorMessage && errorId) || undefined
                }
                className={cn(
                  "accent-dsa-red size-4 border border-gray-400 bg-white",
                  fieldFocusCn(errorMessage),
                )}
              />
              <span className={fieldLabelClassName}>{option.label}</span>
            </label>
          );
        })}
      </div>

      {errorMessage ? (
        <FieldErrorMessage id={errorId} className="mt-2">
          {errorMessage}
        </FieldErrorMessage>
      ) : null}
    </fieldset>
  );
};

export default RadioGroup;
